// Phase E1's screens on @rpg/ui (decisions 0060, 0046): the codex (tabs for the bestiary, the lore and the glyph
// collection; an entry not yet found shows as unknown) and the achievements (each with its badge, its detail and its
// progress, a hidden one as a placeholder until unlocked), opened from the pause screen. Every text from the strings
// table; badges and glyph slots drawn in SVG in the valley palette (decorative: each row's title names it).
import { h, List, ListRow, Meter, Screen, Status, TabPanel, Tabs, type ScreenContext, type ScreenRender, type VNode } from "@rpg/ui";
import type { SpikeContent } from "../content";
import type { MetaLayer } from "./module";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type El = VNode<any>;

/** The badges' paths in a 24-unit box. */
const BADGES: Record<string, string[]> = {
  blade: ["M5 19 17 5l2 2-12 12z", "M4 16l4 4"],
  crown: ["M4 17 3 7l5 4 4-6 4 6 5-4-1 10z", "M5 20h14"],
  leaf: ["M4 20C4 10 11 4 20 4c0 9-6 16-16 16z", "M5 19 15 9"],
  glyph: ["M12 2 21 12 12 22 3 12z", "M12 7v10M8 12h8"],
  book: ["M4 5c3-1 6-1 8 1v14c-2-2-5-2-8-1z", "M20 5c-3-1-6-1-8 1M20 5v14c-3-1-6-1-8 1"],
  compass: ["M12 3a9 9 0 1 0 0 18 9 9 0 1 0 0-18z", "M15 9l-2 4-4 2 2-4z"],
  star: ["M12 3l2.6 5.6 6 .7-4.5 4.1 1.3 6-5.4-3.1-5.4 3.1 1.3-6L3.4 9.3l6-.7z"],
  hand: ["M7 21v-8l-2-3 1-1 3 3V5a1.5 1.5 0 0 1 3 0v6-8a1.5 1.5 0 0 1 3 0v8-6a1.5 1.5 0 0 1 3 0v10c0 4-2 6-5 6z"],
  scroll: ["M6 4h11a2 2 0 0 1 0 4H8v12a2 2 0 0 1-4 0V6a2 2 0 0 1 2-2z", "M10 12h6M10 16h6"],
  question: ["M12 3a9 9 0 1 0 0 18 9 9 0 1 0 0-18z", "M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1 .9-1 1.7M12 17v.1"],
};

/** A badge: the icon in gold when unlocked, muted when locked. */
export function Badge(p: { icon: string; on: boolean }): El {
  const paths = BADGES[p.icon] ?? BADGES.star!;
  const fill = p.on ? "#e9c46a" : "#4b5a4e";
  const ink = p.on ? "#1d2a1f" : "#8a978c";
  return h(
    "svg",
    { viewBox: "0 0 24 24", "aria-hidden": "true", focusable: "false", "data-badge": p.icon, "data-on": p.on ? "" : undefined },
    paths.map((d, i) => h("path", { d, fill: i === 0 ? fill : "none", stroke: i === 0 ? ink : fill, "stroke-width": i === 0 ? 1.2 : 1.6, "stroke-linejoin": "round", "stroke-linecap": "round" })),
  );
}

/** One glyph slot: filled when found. */
function GlyphSlot(p: { on: boolean }): El {
  return h(
    "svg",
    { viewBox: "0 0 24 24", "aria-hidden": "true", focusable: "false", class: "gv-glyph-slot", "data-on": p.on ? "" : undefined },
    h("path", { d: BADGES.glyph![0]!, fill: p.on ? "#9fd8ff" : "none", stroke: p.on ? "#1d2a1f" : "#8a978c", "stroke-width": 1.4, "stroke-dasharray": p.on ? undefined : "2 2" }),
    p.on ? h("path", { d: BADGES.glyph![1]!, fill: "none", stroke: "#1d2a1f", "stroke-width": 1.6, "stroke-linecap": "round" }) : null,
  );
}

const CSS = `
.rpg-screen[data-screen=codex] .gv-codex-body{margin:.15em 0 .2em;color:var(--rpg-text);font-size:.95em;line-height:1.45}
.gv-glyph-row{display:flex;gap:.5em;flex-wrap:wrap;margin:.6em 0}
.gv-glyph-slot{width:2.4em;height:2.4em}
.rpg-list-icon svg{width:100%;height:100%}
`;

/** The codex's and the achievements' screens, and the pause screen's entries for them. */
export function metaScreens(o: { content: SpikeContent; layer: () => MetaLayer; glyphs: () => number }): { screens: Record<string, ScreenRender>; pause: { label: string; action: string; screen: string }[] } {
  if (!document.getElementById("gv-meta-css")) {
    const s = document.createElement("style");
    s.id = "gv-meta-css";
    s.textContent = CSS;
    document.head.appendChild(s);
  }
  const t = o.content.t;
  const rows = () => o.layer().content.codexRows;
  let tab = "bestiary";

  const entryRow = (id: string): El => {
    const l = o.layer();
    const e = l.codex.entry(id);
    const r = rows().find((x) => x.id === id)!;
    if (!e.found) return h(ListRow, { id: `codex-${id}`, title: t("codex.unknown"), detail: t("codex.unknown.detail"), icon: h(Badge, { icon: "question", on: false }) });
    const marks =
      e.section === "bestiary"
        ? t("codex.marks", { met: e.marks.met ?? 0, defeated: e.marks.defeated ?? 0 })
        : t(e.marks.heard ? "codex.mark.heard" : "codex.mark.found");
    return h(ListRow, { id: `codex-${id}`, title: t(r.name.id), badge: marks, detail: t(r.body.id), icon: h(Badge, { icon: e.section === "bestiary" ? "blade" : "scroll", on: true }) });
  };

  const codex: ScreenRender = (ctx: ScreenContext) => {
    const l = o.layer();
    const sections = ["bestiary", "lore"] as const;
    let body: El[];
    if (tab === "glyphs") {
      const max = l.content.maxGlyphs;
      const have = Math.min(max, o.glyphs());
      body = [
        h(Meter, { id: "codex-glyphs", label: t("codex.tab.glyphs"), value: have, max, valueText: t("codex.glyphs.count", { count: have, max }) }),
        h("div", { class: "gv-glyph-row", "data-glyphs": have }, ...Array.from({ length: max }, (_, i) => h(GlyphSlot, { on: i < have }))),
        h(Status, { text: t("codex.glyphs.detail") }),
      ];
    } else {
      const sec = tab as (typeof sections)[number];
      const n = l.codex.count(sec);
      body = [
        h(Meter, { id: `codex-${sec}`, label: t(`codex.tab.${sec}`), value: n.found, max: n.total, valueText: t("codex.count", { found: n.found, total: n.total }) }),
        h(List, { id: `codex-${sec}`, label: t(`codex.tab.${sec}`) }, ...l.codex.section(sec).map((e) => entryRow(e.id))),
      ];
    }
    return h(
      Screen,
      { ctx, title: t("codex.title"), dim: true },
      h(Tabs, {
        id: "codex-tabs",
        label: t("codex.tabs"),
        tabs: [...sections.map((s) => ({ id: s, label: t(`codex.tab.${s}`) })), { id: "glyphs", label: t("codex.tab.glyphs") }],
        selected: tab,
        onSelect: (id: string) => {
          tab = id;
          ctx.ui.refresh();
        },
      }),
      h(TabPanel, { id: "codex-tabs", selected: tab }, ...body),
    );
  };

  const achievements: ScreenRender = (ctx: ScreenContext) => {
    const l = o.layer();
    const n = l.achievements.count();
    const byId = new Map(l.content.achievementRows.map((r) => [r.id, r]));
    return h(
      Screen,
      { ctx, title: t("ach.title"), dim: true },
      h(Meter, { id: "ach-count", label: t("ach.title"), value: n.unlocked, max: n.total, valueText: t("ach.count", n) }),
      h(
        List,
        { id: "achievements", label: t("ach.title") },
        ...l.achievements.list().map((p) => {
          const r = byId.get(p.id)!;
          if (p.hidden && !p.unlocked) return h(ListRow, { id: `ach-${p.id}`, title: t("ach.hidden.title"), detail: t("ach.hidden.detail"), badge: t("ach.locked"), icon: h(Badge, { icon: "question", on: false }) });
          const badge = p.unlocked ? t("ach.unlocked") : p.target !== null && p.target > 1 ? t("ach.progress", { value: Math.min(p.value, p.target), target: p.target }) : t("ach.locked");
          return h(ListRow, { id: `ach-${p.id}`, title: t(r.name.id), detail: t(r.detail.id), badge, icon: h(Badge, { icon: r.icon, on: p.unlocked }) });
        }),
      ),
    );
  };

  return {
    screens: { codex, achievements },
    pause: [
      { label: "pause.codex", action: "codex", screen: "codex" },
      { label: "pause.achievements", action: "achievements", screen: "achievements" },
    ],
  };
}
